import type { InboxView, PersonCard } from "./types";

/** An inbox entry with a stable key (the view has no ids; the list is newest first). */
export interface InboxEntry { key: string; item: InboxView }
export interface InboxWeek { week: number; entries: InboxEntry[] }

/**
 * Keys count back from the oldest message, so an entry keeps its key when newer messages arrive
 * (the list is newest first, so index 0 is the newest).
 */
export function keyInbox(inbox: InboxView[]): InboxEntry[] {
  return inbox.map((item, i) => ({ key: `m${inbox.length - 1 - i}`, item }));
}

/** Group newest first by week; within a week the original (newest first) order is kept. */
export function groupByWeek(entries: InboxEntry[]): InboxWeek[] {
  const byWeek = new Map<number, InboxEntry[]>();
  for (const e of entries) {
    const list = byWeek.get(e.item.week);
    if (list) list.push(e);
    else byWeek.set(e.item.week, [e]);
  }
  return [...byWeek.entries()].sort((a, b) => b[0] - a[0]).map(([week, list]) => ({ week, entries: list }));
}

/** Everyone who has written or spoken to you, alphabetically, for the person filter. */
export function inboxSenders(inbox: InboxView[]): PersonCard[] {
  const seen = new Map<string, PersonCard>();
  for (const m of inbox) if (m.from && !seen.has(m.from.id)) seen.set(m.from.id, m.from);
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name, "en-GB"));
}

/** Sentinel for messages without a named sender. */
export const NO_SENDER = "__none";

/** Filter by sender id ("" = everyone, NO_SENDER = no named sender) and free text (title, message, reply, name). */
export function filterInbox(entries: InboxEntry[], personId: string, query: string): InboxEntry[] {
  const q = query.trim().toLocaleLowerCase("en-GB");
  return entries.filter(({ item }) => {
    if (personId === NO_SENDER ? item.from !== null : personId !== "" && item.from?.id !== personId) return false;
    if (!q) return true;
    return [item.title, item.text, item.reply, item.from?.name ?? ""].some((s) => s.toLocaleLowerCase("en-GB").includes(q));
  });
}

/** How a message reached you: a glyph (decorative) and a word (always shown, never the glyph alone). */
export function channelInfo(channel: string): { glyph: string; word: string } {
  switch (channel) {
    case "email": return { glyph: "✉", word: "Email" };
    case "phone": return { glyph: "☏", word: "Phone" };
    case "text": return { glyph: "▯", word: "Text" };
    case "meeting": return { glyph: "◎", word: "Meeting" };
    case "note": return { glyph: "✎", word: "Note" };
    case "door": return { glyph: "⌂", word: "In person" };
    default: {
      const w = channel.replace(/_/g, " ");
      return { glyph: "•", word: w.charAt(0).toUpperCase() + w.slice(1) };
    }
  }
}
